import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch, ApiError } from '../../lib/api'
import type { DocumentRef, ReaderContext } from '../../lib/documentReader'
import DocumentLink from '../documents/DocumentLink'

interface RequestReview {
  id: string
  title: string
  status: string
  reviewed_binding: string
}
interface Choices {
  documents: DocumentRef[]
  next_offset: number | null
}

export default function EvidenceRequestActions({
  requestId,
  context,
  disabled,
  onChanged,
  onBusy,
}: {
  requestId: string
  context: ReaderContext
  disabled: boolean
  onChanged: () => void
  onBusy: (busy: boolean) => void
}) {
  const [mode, setMode] = useState<'fulfil' | 'waive' | null>(null)
  const [review, setReview] = useState<RequestReview | null>(null)
  const [documents, setDocuments] = useState<DocumentRef[]>([])
  const [nextOffset, setNextOffset] = useState<number | null>(null)
  const [selected, setSelected] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const path = `/api/evidence-gap-requests/${encodeURIComponent(requestId)}`
  function fail(error: unknown) {
    const detail = error instanceof ApiError ? (error.body as { detail?: unknown })?.detail : null
    setError(
      typeof detail === 'string'
        ? detail
        : 'The request changed or is unavailable. Reopen it before trying again.'
    )
  }
  async function open(nextMode: 'fulfil' | 'waive') {
    if (disabled || busy) return
    setBusy(true)
    onBusy(true)
    setError(null)
    try {
      const current = await apiFetch<RequestReview>(path)
      if (!mounted.current) return
      if (current.status !== 'open') {
        onChanged()
        return
      }
      const choices = nextMode === 'fulfil' ? await apiFetch<Choices>(`${path}/documents`) : null
      if (!mounted.current) return
      setReview(current)
      setMode(nextMode)
      setSelected('')
      setReason('')
      setDocuments(choices?.documents ?? [])
      setNextOffset(choices?.next_offset ?? null)
    } catch (error) {
      if (mounted.current) fail(error)
    } finally {
      if (mounted.current) {
        setBusy(false)
        onBusy(false)
      }
    }
  }
  async function more() {
    if (nextOffset === null || disabled || busy) return
    setBusy(true)
    onBusy(true)
    try {
      const choices = await apiFetch<Choices>(`${path}/documents?offset=${nextOffset}`)
      if (!mounted.current) return
      setDocuments((current) => [
        ...new Map(
          [...current, ...choices.documents].map((doc) => [doc.document_id, doc])
        ).values(),
      ])
      setNextOffset(choices.next_offset)
    } catch (error) {
      if (mounted.current) fail(error)
    } finally {
      if (mounted.current) {
        setBusy(false)
        onBusy(false)
      }
    }
  }
  async function submit() {
    if (disabled || busy || !review || !mode || (mode === 'waive' ? !reason.trim() : !selected))
      return
    setBusy(true)
    onBusy(true)
    setError(null)
    try {
      await apiFetch(`${path}/${mode}`, {
        method: 'POST',
        body: JSON.stringify({
          reviewed_binding: review.reviewed_binding,
          ...(mode === 'waive' ? { reason: reason.trim() } : { document_id: selected }),
        }),
      })
      if (!mounted.current) return
      setMode(null)
      setReview(null)
      onChanged()
    } catch (error) {
      if (mounted.current) fail(error)
    } finally {
      if (mounted.current) {
        setBusy(false)
        onBusy(false)
      }
    }
  }
  return (
    <div>
      {!mode ? (
        <>
          <button
            type="button"
            className="cn-btn"
            disabled={disabled || busy}
            onClick={() => void open('fulfil')}
          >
            Supply evidence
          </button>{' '}
          <button
            type="button"
            className="cn-btn"
            disabled={disabled || busy}
            onClick={() => void open('waive')}
          >
            Record waiver
          </button>
        </>
      ) : (
        <>
          {mode === 'waive' ? (
            <label>
              Reason for the waiver
              <textarea
                value={reason}
                maxLength={4000}
                disabled={disabled || busy}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
          ) : (
            <div aria-label="Choose evidence for this request">
              {documents.map((document) => (
                <div key={document.document_id}>
                  <label>
                    <input
                      type="radio"
                      name={`evidence-${requestId}`}
                      checked={selected === document.document_id}
                      disabled={disabled || busy}
                      onChange={() => setSelected(document.document_id)}
                    />{' '}
                    Select
                  </label>{' '}
                  <DocumentLink document={document} context={context} />
                </div>
              ))}
              {!documents.length && <p>No available documents in this request’s scope.</p>}
              {nextOffset !== null && (
                <button
                  type="button"
                  className="cn-btn"
                  disabled={disabled || busy}
                  onClick={() => void more()}
                >
                  More documents
                </button>
              )}
              <Link to="/v2/traces?view=documents">Open Documents or upload a document →</Link>
            </div>
          )}
          <button
            type="button"
            className="cn-btn"
            disabled={disabled || busy || (mode === 'waive' ? !reason.trim() : !selected)}
            onClick={() => void submit()}
          >
            {mode === 'waive' ? 'Record waiver with reason' : 'Use selected document'}
          </button>{' '}
          <button
            type="button"
            className="cn-btn"
            disabled={disabled || busy}
            onClick={() => {
              setMode(null)
              setReview(null)
              setError(null)
            }}
          >
            Cancel
          </button>
        </>
      )}
      {error && (
        <p className="cn-errline" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
